'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdInventory2,
    MdWarning,
    MdThermostat,
    MdLocalPharmacy,
    MdTrendingUp,
    MdTrendingDown,
    MdRefresh,
    MdMedication,
    MdSecurity,
} from 'react-icons/md'
import {
    getStockOverview,
    getStockValue,
    getLowStockItems,
    getExpiringBatchesForDashboard,
    getTemperatureStatus,
    getDispensationStats,
    StockOverview,
    StockValue,
    LowStockItem,
    ExpiringBatch,
    TemperatureStatus,
    DispensationStats,
} from '@/lib/api/stock/dashboard-api'

export default function StockDashboardPage() {
    const navigate = useNavigate()
    const [overview, setOverview] = useState<StockOverview | null>(null)
    const [stockValue, setStockValue] = useState<StockValue | null>(null)
    const [lowStock, setLowStock] = useState<LowStockItem[]>([])
    const [expiring, setExpiring] = useState<ExpiringBatch[]>([])
    const [temperature, setTemperature] = useState<TemperatureStatus[]>([])
    const [dispensationStats, setDispensationStats] =
        useState<DispensationStats | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        loadDashboard()
    }, [])

    const loadDashboard = async () => {
        setLoading(true)
        try {
            const [
                overviewRes,
                valueRes,
                lowStockRes,
                expiringRes,
                tempRes,
                dispRes,
            ] = await Promise.all([
                getStockOverview(),
                getStockValue(),
                getLowStockItems(5),
                getExpiringBatchesForDashboard(90, 5),
                getTemperatureStatus(),
                getDispensationStats(30),
            ])

            if (overviewRes.success) setOverview(overviewRes.result)
            if (valueRes.success) setStockValue(valueRes.result)
            if (lowStockRes.success) setLowStock(lowStockRes.result)
            if (expiringRes.success) setExpiring(expiringRes.result)
            if (tempRes.success) setTemperature(tempRes.result)
            if (dispRes.success) setDispensationStats(dispRes.result)
        } catch (error) {
            toast.error('Erro ao carregar dashboard')
        } finally {
            setLoading(false)
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR')
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dashboard...
                </p>
            </div>
        )
    }

    const hasTemperatureAlerts = temperature.some(t => !t.isInRange)

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdInventory2 className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Dashboard de Estoque
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Visão geral do controle de estoque
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-ghost btn-sm gap-2"
                    onClick={loadDashboard}
                >
                    <MdRefresh className="text-lg" />
                    Atualizar
                </button>
            </div>

            {/* Alertas */}
            {((overview?.temperatureAlerts ?? 0) > 0 ||
                (overview?.lowStockCount ?? 0) > 0 ||
                (overview?.pendingSngpc ?? 0) > 0) && (
                    <div className="flex flex-col gap-2">
                        {(overview?.temperatureAlerts ?? 0) > 0 && (
                            <div
                                className="alert alert-error cursor-pointer"
                                onClick={() => navigate('/stock/temperature')}
                            >
                                <MdThermostat className="text-2xl" />
                                <span>
                                    {overview?.temperatureAlerts} alertas de
                                    temperatura ativos...
                                </span>
                            </div>
                        )}
                        {(overview?.lowStockCount ?? 0) > 0 && (
                            <div
                                className="alert alert-warning cursor-pointer"
                                onClick={() => navigate('/stock/medications')}
                            >
                                <MdWarning className="text-2xl" />
                                <span>
                                    {overview?.lowStockCount} medicamentos com
                                    estoque baixo...
                                </span>
                            </div>
                        )}
                        {(overview?.pendingSngpc ?? 0) > 0 && (
                            <div
                                className="alert alert-info cursor-pointer"
                                onClick={() => navigate('/stock/sngpc')}
                            >
                                <MdSecurity className="text-2xl" />
                                <span>
                                    {overview?.pendingSngpc} movimentações SNGPC
                                    pendentes...
                                </span>
                            </div>
                        )}
                    </div>
                )}

            {/* Stats principais */}
            <div className="stats shadow bg-base-100 border border-base-300/50 stats-vertical sm:stats-horizontal">
                <div
                    className="stat cursor-pointer hover:bg-base-200"
                    onClick={() => navigate('/stock/medications')}
                >
                    <div className="stat-figure text-secondary">
                        <MdMedication className="text-3xl" />
                    </div>
                    <div className="stat-title">Medicamentos</div>
                    <div className="stat-value text-secondary">
                        {overview?.totalMedications || 0}
                    </div>
                    <div className="stat-desc">
                        {overview?.controlledMedications || 0} controlados
                    </div>
                </div>
                <div
                    className="stat cursor-pointer hover:bg-base-200"
                    onClick={() => navigate('/stock/batches')}
                >
                    <div className="stat-figure text-secondary">
                        <MdInventory2 className="text-3xl" />
                    </div>
                    <div className="stat-title">Lotes Ativos</div>
                    <div className="stat-value text-secondary">
                        {overview?.activeBatches || 0}
                    </div>
                    <div className="stat-desc">
                        {overview?.expiringCount || 0} vencendo em 90 dias
                    </div>
                </div>
                <div className="stat">
                    <div className="stat-figure text-accent">
                        <MdTrendingUp className="text-3xl" />
                    </div>
                    <div className="stat-title">Valor em Estoque</div>
                    <div className="stat-value text-accent text-2xl">
                        {formatCurrency(stockValue?.totalValue || 0)}
                    </div>
                    <div className="stat-desc">
                        {stockValue?.totalItems || 0} itens
                    </div>
                </div>
                <div
                    className="stat cursor-pointer hover:bg-base-200"
                    onClick={() => navigate('/stock/dispensation')}
                >
                    <div className="stat-figure text-info">
                        <MdLocalPharmacy className="text-3xl" />
                    </div>
                    <div className="stat-title">Dispensações</div>
                    <div className="stat-value text-info">
                        {overview?.pendingDispensations || 0}
                    </div>
                    <div className="stat-desc">pendentes</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Estoque Baixo */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <MdTrendingDown className="text-warning" />
                            Estoque Baixo
                        </h2>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate('/stock/medications')}
                        >
                            Ver todos
                        </button>
                    </div>
                    {lowStock.length === 0 ? (
                        <p className="text-center text-base-content/60 py-8">
                            Nenhum medicamento com estoque baixo
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {lowStock.map(item => (
                                <div
                                    key={item.id}
                                    className="flex items-center justify-between p-3 bg-base-200/50 rounded-lg"
                                >
                                    <div>
                                        <p className="font-semibold">
                                            {item.name}
                                        </p>
                                        {item.genericName && (
                                            <p className="text-sm text-base-content/60">
                                                {item.genericName}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-error">
                                            {item.currentStock}
                                        </p>
                                        <p className="text-xs text-base-content/60">
                                            Min: {item.minimumStock}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Vencendo */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <MdWarning className="text-warning" />
                            Próximos do Vencimento
                        </h2>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate('/stock/batches')}
                        >
                            Ver todos
                        </button>
                    </div>
                    {expiring.length === 0 ? (
                        <p className="text-center text-base-content/60 py-8">
                            Nenhum lote proximo do vencimento
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {expiring.map(batch => (
                                <div
                                    key={batch.id}
                                    className="flex items-center justify-between p-3 bg-base-200/50 rounded-lg"
                                >
                                    <div>
                                        <p className="font-semibold">
                                            {batch.medication.name}
                                        </p>
                                        <p className="text-sm text-base-content/60">
                                            Lote: {batch.batchNumber}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-warning">
                                            {formatDate(batch.expirationDate)}
                                        </p>
                                        <p className="text-xs text-base-content/60">
                                            Qtd: {batch.currentQuantity}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Temperatura */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <MdThermostat
                                className={
                                    hasTemperatureAlerts
                                        ? 'text-error'
                                        : 'text-success'
                                }
                            />
                            Monitoramento de Temperatura
                        </h2>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate('/stock/temperature')}
                        >
                            Ver detalhes
                        </button>
                    </div>
                    {temperature.length === 0 ? (
                        <p className="text-center text-base-content/60 py-8">
                            Nenhuma unidade de armazenamento
                        </p>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            {temperature.slice(0, 4).map(unit => (
                                <div
                                    key={unit.id}
                                    className={`p-3 rounded-lg ${unit.isInRange === false ? 'bg-error/10' : 'bg-success/10'}`}
                                >
                                    <p className="font-semibold text-sm">
                                        {unit.name}
                                    </p>
                                    <p
                                        className={`text-2xl font-bold ${unit.isInRange === false ? 'text-error' : 'text-success'}`}
                                    >
                                        {unit.currentTemperature
                                            ? `${unit.currentTemperature.toFixed(1)}C`
                                            : '--'}
                                    </p>
                                    <p className="text-xs text-base-content/60">
                                        {unit.minTemp}C - {unit.maxTemp}C
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Dispensacao Stats */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <MdLocalPharmacy className="text-info" />
                            Dispensações (30 dias)
                        </h2>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate('/stock/dispensation')}
                        >
                            Ver todas
                        </button>
                    </div>
                    {dispensationStats ? (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span>Total</span>
                                <span className="font-bold text-xl">
                                    {dispensationStats.total}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span>Finalizadas</span>
                                <span className="font-bold text-success">
                                    {dispensationStats.completed}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span>Pendentes</span>
                                <span className="font-bold text-warning">
                                    {dispensationStats.pending}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span>Canceladas</span>
                                <span className="font-bold text-error">
                                    {dispensationStats.cancelled}
                                </span>
                            </div>
                            <div className="pt-2 border-t border-base-300/50">
                                <div className="flex justify-between items-center">
                                    <span>Taxa de Conclusão</span>
                                    <span className="font-bold text-secondary">
                                        {dispensationStats.completionRate.toFixed(
                                            1
                                        )}
                                        %
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-center text-base-content/60 py-8">
                            Sem dados de dispensação
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}
